import React, { useEffect, useRef, useState } from 'react'

import * as styles from './ListingCard.module.scss'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { truncateText } from 'src/utilities/truncateText'

export interface ListingCardProps {
  date?: string
  tag?: string
  imageSrc?: string
  gatsbyImage?: any
  title: string
  titleSize?: 'default' | 'sm'
  description?: string
  url: string
  formatDate?: boolean
  isFeatured?: boolean
  truncateSize?: number
}

const ListingCard: React.FC<ListingCardProps> = ({
  date,
  tag,
  imageSrc,
  gatsbyImage,
  title,
  titleSize = 'default',
  description,
  url,
  formatDate = false,
  isFeatured = false,
  truncateSize = 100
}) => {
  const formattedDate = formatDate
    ? new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : date

  const cardRef = useRef<HTMLDivElement | null>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth >= 768) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting)
      },
      {
        threshold: 1 // Adjust based on how "visible" it should be to trigger
      }
    )

    if (cardRef.current) observer.observe(cardRef.current)

    return () => observer.disconnect()
  }, [])

  return (
    <div
      className={`${styles.listingCard} ${
        isFeatured ? styles.isFeatured : ''
      } ${inView ? styles.inView : ''}`}
      ref={cardRef}
    >
      {tag && (
        <span className={styles.listingCardTag}>
          {tag.charAt(0).toUpperCase() + tag.slice(1)}
        </span>
      )}
      {gatsbyImage && (
        <div className={styles.listingCardImage}>
          <GatsbyImage
            image={getImage(gatsbyImage)}
            alt=''
            className='gatsbyImage'
          />
        </div>
      )}

      {imageSrc && (
        <div className={styles.listingCardImage}>
          <img src={imageSrc} alt='' />
        </div>
      )}

      <h4
        className={
          styles.listingCardTitle +
          (titleSize === 'sm' ? ' ' + styles.listingCardTitleSm : '')
        }
      >
        {title}
      </h4>
      {date && <p className={styles.listingCardDate}>{formattedDate}</p>}
      <p className={styles.listingCardDescription}>
        {description && truncateText(description, truncateSize)}
      </p>

      <a href={`${url}`} className={styles.listingCardLink}>
        <span className='visually-hidden'>View link</span>
      </a>
    </div>
  )
}

export default ListingCard
